// components/MyMarketSense.jsx
// Aggregate Market Sense page — cross-stock prediction tracking
import React, { useState, useEffect } from 'react';
import { theme } from '../theme';
import { useAuth } from '../AuthContext';
import { getMarketSenseData, getAllPredictions, getPredictedTickers, settlePredictions } from '../firebase';

const RANKS = [
  { id: 'bronze',   label: 'BRONZE',   min: 60, max: 65, badge: '/badge_B.png', color: '#cd7f32' },
  { id: 'silver',   label: 'SILVER',   min: 65, max: 70, badge: '/badge_S.png', color: '#c0c0c0' },
  { id: 'gold',     label: 'GOLD',     min: 70, max: 75, badge: '/badge_G.png', color: '#e3b341' },
  { id: 'platinum', label: 'PLATINUM', min: 75, max: 80, badge: '/badge_P.png', color: '#a8b4c4' },
  { id: 'diamond',  label: 'DIAMOND',  min: 80, max: 100, badge: '/badge_D.png', color: '#b9f2ff' },
];

function getRank(score) {
  for (const rank of RANKS) {
    if (score < rank.max || rank.id === 'diamond') return rank;
  }
  return RANKS[0];
}

function getNextRank(score) {
  const current = getRank(score);
  const idx = RANKS.findIndex(r => r.id === current.id);
  if (idx < RANKS.length - 1) {
    const next = RANKS[idx + 1];
    return { rank: next, pointsNeeded: next.min - score };
  }
  return null;
}

const TIER_COLORS = {
  'big_rise': '#22a06b', 'small_rise': '#57d9a3', 'flat': '#8b949e',
  'small_drop': '#e07a5f', 'big_drop': '#cf4444',
};
const TIER_LABELS = {
  'big_rise': 'Big Rise', 'small_rise': 'Small Rise', 'flat': 'Flat',
  'small_drop': 'Small Drop', 'big_drop': 'Big Drop',
};

export function MyMarketSense({ onBack }) {
  const { user } = useAuth();
  const [msData, setMsData] = useState(null);
  const [allPredictions, setAllPredictions] = useState([]);
  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [filterTicker, setFilterTicker] = useState('ALL');

  useEffect(() => {
    if (!user) return;
    setLoading(true);

    // Step 1: Settle any pending predictions from previous days
    // Step 2: Then load fresh data
    settlePredictions(user.uid)
      .then((settleResult) => {
        if (settleResult.settled > 0) {
          console.log(`Settled ${settleResult.settled} predictions, IQ delta: ${settleResult.iqDelta}`);
        }
        // Now load data (with updated settlements)
        return Promise.all([
          getMarketSenseData(user.uid),
          getAllPredictions(user.uid, 100),
          getPredictedTickers(user.uid),
        ]);
      })
      .then(([ms, preds, tkrs]) => {
        setMsData(ms);
        setAllPredictions(preds);
        setTickers(tkrs.sort());
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: theme.textMuted, fontSize: '20px' }}>Please sign in to view your Market Sense data.</p>
      </div>
    );
  }

  const iqScore = msData?.iqScore || 150;
  const streak = msData?.streak || 0;
  const bestStreak = msData?.bestStreak || 0;
  const moodStats = msData?.moodStats || {};
  const currentRank = getRank(iqScore);
  const nextRankInfo = getNextRank(iqScore);

  // Filter predictions by time range and ticker
  const filtered = allPredictions.filter(p => {
    if (filterTicker !== 'ALL' && p.ticker !== filterTicker) return false;
    if (timeRange === 7) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return new Date(p.date) >= sevenDaysAgo;
    }
    if (timeRange === 30) {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return new Date(p.date) >= thirtyDaysAgo;
    }
    return true;
  });

  // Stats from filtered predictions
  const totalPredictions = filtered.length;
  const settled = filtered.filter(p => p.userCorrect !== null);
  const correct = settled.filter(p => p.userCorrect === true);
  const accuracy = settled.length > 0 ? Math.round((correct.length / settled.length) * 100) : 0;
  const pending = filtered.filter(p => p.actualChange === null).length;

  // Mood breakdown from filtered
  const moodBreakdown = { calm: 0, confident: 0, anxious: 0, impulsive: 0 };
  filtered.forEach(p => { if (p.mood && moodBreakdown[p.mood] !== undefined) moodBreakdown[p.mood]++; });
  const maxMood = Math.max(1, ...Object.values(moodBreakdown));

  // Per-ticker accuracy
  const tickerStats = {};
  allPredictions.forEach(p => {
    if (!p.ticker) return;
    if (!tickerStats[p.ticker]) tickerStats[p.ticker] = { total: 0, settled: 0, correct: 0 };
    tickerStats[p.ticker].total++;
    if (p.userCorrect !== null) {
      tickerStats[p.ticker].settled++;
      if (p.userCorrect) tickerStats[p.ticker].correct++;
    }
  });
  const tickerAccuracy = Object.entries(tickerStats)
    .map(([ticker, s]) => ({ ticker, ...s, accuracy: s.settled > 0 ? Math.round((s.correct / s.settled) * 100) : null }))
    .sort((a, b) => (b.accuracy || 0) - (a.accuracy || 0));

  // Check-in: how many unique dates
  const uniqueDates = new Set(allPredictions.map(p => p.date));
  const totalCheckIns = uniqueDates.size;
  const today = new Date().toISOString().split('T')[0];
  const hasCheckedInToday = uniqueDates.has(today);

  // Toggle
  const RangeToggle = () => (
    <div style={{ display: 'flex', gap: '4px', background: theme.bg, borderRadius: '8px', padding: '3px' }}>
      {[7, 30].map(d => (
        <button key={d} onClick={() => setTimeRange(d)} style={{
          padding: '6px 16px', borderRadius: '6px', border: 'none', fontSize: '15px', fontWeight: 600,
          background: timeRange === d ? theme.teal : 'transparent',
          color: timeRange === d ? '#0f172a' : theme.textDark,
          cursor: 'pointer', transition: 'all 0.15s ease',
        }}>
          {d}d
        </button>
      ))}
    </div>
  );

  const moodItems = [
    { key: 'calm',      label: 'Calm',      icon: '🧘', color: '#22c55e', positive: true },
    { key: 'confident', label: 'Confident', icon: '💪', color: '#57d9a3', positive: true },
    { key: 'anxious',   label: 'Anxious',   icon: '😰', color: '#e07a5f', positive: false },
    { key: 'impulsive', label: 'Impulsive', icon: '⚡', color: '#cf4444', positive: false },
  ];
  const barMaxHeight = 70;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg }}>
      {/* Header */}
      <nav style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 32px', borderBottom: '1px solid ' + theme.border }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: '1px solid ' + theme.border, borderRadius: '10px', padding: '10px 20px', color: theme.textMuted, fontSize: '16px', cursor: 'pointer' }}>← Back</button>
          <span style={{ fontFamily: 'Georgia, serif', fontSize: '28px', fontWeight: 400, color: theme.text }}>My Market Sense</span>
        </div>
      </nav>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ width: '40px', height: '40px', border: '3px solid ' + theme.border, borderTop: '3px solid ' + theme.teal, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <p style={{ color: theme.textDark, fontSize: '18px' }}>Loading your data...</p>
          </div>
        ) : (
          <>
            {/* Row 1: IQ Score + Check-in + Overview Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '24px' }}>

              {/* IQ Score Card */}
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '28px', display: 'flex', alignItems: 'center', gap: '24px' }}>
                <div style={{ flexShrink: 0, textAlign: 'center' }}>
                  <img src={currentRank.badge} alt={currentRank.label}
                    style={{ width: '120px', height: '120px', objectFit: 'contain', marginBottom: '8px' }} />
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '4px' }}>
                    {RANKS.map(rank => (
                      <img key={rank.id} src={rank.badge} alt={rank.label}
                        style={{ width: '28px', height: '28px', objectFit: 'contain', opacity: rank.id === currentRank.id ? 1 : 0.2, filter: rank.id === currentRank.id ? 'none' : 'grayscale(100%)' }} />
                    ))}
                  </div>
                </div>
                <div>
                  <p style={{ color: theme.textDark, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px' }}>Your IQ Score</p>
                  <p style={{ fontSize: '52px', fontWeight: 700, color: theme.teal, margin: '0 0 2px', lineHeight: 1 }}>{iqScore}</p>
                  <p style={{ fontSize: '20px', fontWeight: 700, color: currentRank.color, letterSpacing: '1px', margin: '0 0 6px' }}>{currentRank.label}</p>
                  {nextRankInfo && (
                    <p style={{ fontSize: '16px', color: theme.textDark, margin: 0 }}>
                      Next: {nextRankInfo.rank.label} at {nextRankInfo.rank.min} (+{nextRankInfo.pointsNeeded} to go)
                    </p>
                  )}
                </div>
              </div>

              {/* Check-in + Streak Card */}
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '28px', display: 'flex', alignItems: 'center', gap: '24px' }}>
                <div style={{ flexShrink: 0, textAlign: 'center' }}>
                  <p style={{ fontSize: '56px', fontWeight: 700, color: hasCheckedInToday ? '#22c55e' : theme.textDark, margin: 0, lineHeight: 1 }}>{totalCheckIns}</p>
                  <p style={{ fontSize: '15px', color: theme.textDark, textTransform: 'uppercase', letterSpacing: '1px', margin: '6px 0 0' }}>Check-ins</p>
                  {hasCheckedInToday && (
                    <p style={{ fontSize: '15px', color: '#22c55e', fontWeight: 600, margin: '4px 0 0' }}>✅ Today</p>
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ color: theme.textDark, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 6px' }}>Accuracy Streak</p>
                  <p style={{ fontSize: '40px', fontWeight: 700, color: streak > 0 ? '#22c55e' : theme.textDark, margin: '0 0 4px', lineHeight: 1 }}>{streak}</p>
                  <p style={{ fontSize: '17px', color: streak > 0 ? '#22c55e' : theme.textDark, margin: '0 0 12px' }}>Consecutive correct days</p>
                  <div style={{ display: 'inline-block', background: 'rgba(45, 212, 191, 0.1)', border: '1px solid rgba(45, 212, 191, 0.3)', borderRadius: '20px', padding: '5px 14px', fontSize: '16px', color: theme.teal }}>
                    Personal best: {bestStreak} days
                  </div>
                </div>
              </div>

              {/* Overview Stats Card */}
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '28px' }}>
                <p style={{ color: theme.textDark, fontSize: '15px', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 16px', fontWeight: 600 }}>Overview</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <p style={{ fontSize: '36px', fontWeight: 700, color: theme.teal, margin: 0, lineHeight: 1 }}>{totalPredictions}</p>
                    <p style={{ fontSize: '15px', color: theme.textDark, margin: '4px 0 0' }}>Total Predictions</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '36px', fontWeight: 700, color: accuracy >= 50 ? '#22c55e' : '#f85149', margin: 0, lineHeight: 1 }}>{accuracy}%</p>
                    <p style={{ fontSize: '15px', color: theme.textDark, margin: '4px 0 0' }}>Accuracy</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '36px', fontWeight: 700, color: theme.text, margin: 0, lineHeight: 1 }}>{tickers.length}</p>
                    <p style={{ fontSize: '15px', color: theme.textDark, margin: '4px 0 0' }}>Stocks Tracked</p>
                  </div>
                  <div>
                    <p style={{ fontSize: '36px', fontWeight: 700, color: theme.textMuted, margin: 0, lineHeight: 1 }}>{pending}</p>
                    <p style={{ fontSize: '15px', color: theme.textDark, margin: '4px 0 0' }}>Pending</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Mood Distribution + Per-Ticker Accuracy */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '24px' }}>

              {/* Mood Distribution */}
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <p style={{ color: theme.text, fontSize: '20px', fontWeight: 700, margin: 0 }}>Mood Distribution</p>
                  <RangeToggle />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: '0', position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '5%', right: '5%', top: '50%', height: '1px', background: theme.border }} />
                  {moodItems.map(item => {
                    const count = moodBreakdown[item.key] || 0;
                    const barH = maxMood > 0 ? (count / maxMood) * barMaxHeight : 0;
                    const stats = moodStats?.[item.key];
                    const pct = stats && stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : null;
                    return (
                      <div key={item.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <div style={{ height: barMaxHeight + 'px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
                          {item.positive && count > 0 && <span style={{ fontSize: '16px', fontWeight: 700, color: item.color, marginBottom: '4px' }}>{count}</span>}
                          {item.positive && <div style={{ width: '44px', height: Math.max(barH, count > 0 ? 4 : 0) + 'px', background: item.color + '50', borderTop: count > 0 ? '3px solid ' + item.color : 'none', borderRadius: '4px 4px 0 0', transition: 'height 0.3s ease' }} />}
                        </div>
                        <div style={{ padding: '8px 0', textAlign: 'center' }}>
                          <span style={{ fontSize: '32px', display: 'block' }}>{item.icon}</span>
                          <span style={{ fontSize: '15px', color: theme.textDark, display: 'block', marginTop: '2px', fontWeight: 500 }}>{item.label}</span>
                          {pct != null && <span style={{ fontSize: '15px', color: item.color, fontWeight: 700, display: 'block' }}>{pct}%</span>}
                        </div>
                        <div style={{ height: barMaxHeight + 'px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
                          {!item.positive && <div style={{ width: '44px', height: Math.max(barH, count > 0 ? 4 : 0) + 'px', background: item.color + '50', borderBottom: count > 0 ? '3px solid ' + item.color : 'none', borderRadius: '0 0 4px 4px', transition: 'height 0.3s ease' }} />}
                          {!item.positive && count > 0 && <span style={{ fontSize: '16px', fontWeight: 700, color: item.color, marginTop: '4px' }}>{count}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Per-Ticker Accuracy */}
              <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '28px' }}>
                <p style={{ color: theme.text, fontSize: '20px', fontWeight: 700, margin: '0 0 20px' }}>Accuracy by Stock</p>
                {tickerAccuracy.length > 0 ? (
                  <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                    {tickerAccuracy.map(t => (
                      <div key={t.ticker} style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid ' + theme.bg }}>
                        <span style={{ fontSize: '18px', fontWeight: 700, color: theme.teal, width: '80px' }}>{t.ticker}</span>
                        <div style={{ flex: 1, height: '8px', background: theme.bg, borderRadius: '4px', marginRight: '16px' }}>
                          <div style={{ height: '100%', width: (t.accuracy || 0) + '%', background: (t.accuracy || 0) >= 50 ? '#22c55e' : '#f85149', borderRadius: '4px', transition: 'width 0.3s ease' }} />
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: 700, color: (t.accuracy || 0) >= 50 ? '#22c55e' : t.accuracy !== null ? '#f85149' : theme.textDark, minWidth: '50px', textAlign: 'right' }}>
                          {t.accuracy !== null ? t.accuracy + '%' : '—'}
                        </span>
                        <span style={{ fontSize: '14px', color: theme.textDark, marginLeft: '8px', minWidth: '60px' }}>
                          ({t.correct}/{t.settled})
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: theme.textDark, fontSize: '16px', textAlign: 'center', padding: '40px 0' }}>No predictions yet.</p>
                )}
              </div>
            </div>

            {/* Row 3: Full Prediction History Table */}
            <div style={{ background: theme.bgCard, border: '1px solid ' + theme.border, borderRadius: '14px', padding: '28px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <p style={{ color: theme.text, fontSize: '20px', fontWeight: 700, margin: 0 }}>Prediction History</p>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  {/* Ticker filter */}
                  <select value={filterTicker} onChange={e => setFilterTicker(e.target.value)}
                    style={{ background: theme.bg, border: '1px solid ' + theme.border, borderRadius: '8px', padding: '6px 14px', color: theme.text, fontSize: '15px', cursor: 'pointer' }}>
                    <option value="ALL">All Stocks</option>
                    {tickers.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <RangeToggle />
                </div>
              </div>

              {filtered.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['Date', 'Stock', 'Your Call', 'AI Call', 'Actual', 'You', 'AI', 'Mood', 'IQ'].map(h => (
                          <th key={h} style={{
                            fontSize: '15px', color: theme.textDark, textTransform: 'uppercase',
                            letterSpacing: '0.5px', textAlign: 'left', padding: '10px 12px',
                            borderBottom: '2px solid ' + theme.border, fontWeight: 600,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row, i) => {
                        const isPending = row.actualChange === null;
                        return (
                          <tr key={i} style={{ borderBottom: '1px solid ' + theme.bg }}>
                            <td style={{ padding: '12px', fontSize: '16px', color: theme.textMuted }}>{row.date}</td>
                            <td style={{ padding: '12px', fontSize: '16px', color: theme.teal, fontWeight: 700 }}>{row.ticker}</td>
                            <td style={{ padding: '12px', fontSize: '16px', color: TIER_COLORS[row.tier] || theme.textMuted, fontWeight: 600 }}>
                              {TIER_LABELS[row.tier] || row.tier}
                            </td>
                            <td style={{ padding: '12px', fontSize: '16px', color: TIER_COLORS[row.aiTier] || theme.textMuted, fontWeight: 600 }}>
                              {TIER_LABELS[row.aiTier] || row.aiTier || '—'}
                            </td>
                            <td style={{ padding: '12px', fontSize: '16px', color: isPending ? theme.textDark : (row.actualChange >= 0 ? '#22c55e' : '#f85149') }}>
                              {isPending ? (
                                <span style={{ background: 'rgba(45, 212, 191, 0.1)', color: theme.teal, padding: '4px 12px', borderRadius: '6px', fontSize: '15px', fontWeight: 600 }}>Pending</span>
                              ) : (
                                (row.actualChange >= 0 ? '+' : '') + row.actualChange + '%'
                              )}
                            </td>
                            <td style={{ padding: '12px', fontSize: '18px', color: isPending ? theme.textDark : (row.userCorrect ? '#22c55e' : '#f85149') }}>
                              {isPending ? '⏳' : (row.userCorrect ? '✓' : '✗')}
                            </td>
                            <td style={{ padding: '12px', fontSize: '18px', color: isPending ? theme.textDark : (row.aiCorrect ? '#22c55e' : '#f85149') }}>
                              {isPending ? '⏳' : (row.aiCorrect ? '✓' : '✗')}
                            </td>
                            <td style={{ padding: '12px', fontSize: '16px', color: theme.textMuted }}>
                              {row.mood ? row.mood.charAt(0).toUpperCase() + row.mood.slice(1) : '—'}
                            </td>
                            <td style={{ padding: '12px', fontSize: '16px', fontWeight: 700,
                              color: isPending ? theme.textDark : (row.iqChange > 0 ? '#22c55e' : row.iqChange < 0 ? '#f85149' : theme.textDark) }}>
                              {isPending ? '—' : ((row.iqChange > 0 ? '+' : '') + row.iqChange)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ color: theme.textDark, fontSize: '18px', textAlign: 'center', padding: '40px 0', margin: 0 }}>
                  No predictions found for this period. Start making calls on the Full Analysis page!
                </p>
              )}
            </div>

            {/* Summary */}
            <div style={{ marginTop: '24px', padding: '20px 24px', background: 'rgba(45, 212, 191, 0.04)', borderRadius: '10px', borderLeft: '3px solid ' + theme.teal }}>
              <p style={{ color: theme.textMuted, fontSize: '20px', lineHeight: '1.6', margin: 0 }}>
                This page shows your aggregate prediction performance across all stocks. Each correct call earns +1 IQ point, and 3 consecutive correct days earns a bonus +1. Track your mood patterns to discover how emotions influence your decision accuracy.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default MyMarketSense;
